import React, { FC } from 'react';

import useToggleState from '@/hooks/useToggleState';

import IconCross from '@/assets/cross.svg?react';
import IconInfo from '@/assets/info-shape.svg?react';
import Avatar from '@/components/ui/Avatar';
import { Guardian } from '@/components/ui/ClientCard';
import cn from '@/utils/cn';

import { useAutoAnimate } from '@formkit/auto-animate/react';

type BaseProps = {
  className?: string;
  description: string | React.ReactNode;
};

type PrimaryProps = BaseProps & {
  variant: 'primary';
  consultant?: Pick<Guardian, 'name' | 'image'> & {
    jobDescription: string;
  };
  hideCloseButton?: boolean;
};

type Props =
  | PrimaryProps
  | (BaseProps & {
      variant: 'secondary';
    });

const ChatMessage: FC<Props> = (props) => {
  const { variant = 'primary', description, className } = props;

  const [close, handleClose] = useToggleState(false);
  const [parent] = useAutoAnimate();

  let consultant;
  let hideCloseButton = false;
  if (variant === 'primary') {
    consultant = (props as PrimaryProps).consultant;
    hideCloseButton = (props as PrimaryProps).hideCloseButton ?? false;
  }

  return (
    <div ref={parent} className={cn(close && 'lg:-mt-5')}>
      {/* added in order to auto animate^ */}
      {close && null}
      {!close && (
        <div
          className={cn(
            'flex gap-2 px-2.5',
            variant === 'primary'
              ? 'flex-col items-center pb-4 md:px-6 lg:flex-row lg:gap-3 lg:px-6 lg:py-0 lg:pt-1.5'
              : 'flex-row rounded-lg rounded-bl-2xl border-l-[6px] border-primary bg-gray-100 p-4',
            className
          )}
        >
          {variant === 'primary' && consultant && (
            <div className="flex w-full min-w-[230px] items-center gap-3 py-0 text-dark-gray-500 lg:max-w-max lg:py-0">
              <Avatar
                img={consultant.image}
                name={consultant.name}
                variant="chat"
              />
              <div className="flex w-full flex-col">
                {/* deferent size on mobile and desktop */}
                <h5 className="hidden text-dark-gray-500 lg:block">
                  {consultant.name}
                </h5>
                <h4 className="text-dark-gray-500 lg:hidden">
                  {consultant.name}
                </h4>
                <div className="caption text-dark-gray-300">
                  {consultant.jobDescription}
                </div>
              </div>
              {!hideCloseButton && (
                <button className="group aspect-square max-w-max cursor-pointer lg:hidden">
                  <IconCross
                    aria-label="close"
                    onClick={handleClose}
                    className={
                      'size-[14px] shrink-0 text-dark-gray-300 transition-colors group-hover:text-dark-gray-500'
                    }
                  />
                </button>
              )}
            </div>
          )}
          {variant === 'secondary' && (
            <IconInfo
              data-testid="secondary-icon"
              className="shrink-0 text-primary"
            />
          )}
          <p className="caption-large mr-auto w-full max-w-[570px] text-left text-dark-gray-500">
            {description}
          </p>
        </div>
      )}
    </div>
  );
};

export default ChatMessage;
